import { pushSubscriptionsService } from '../supabase/push-subscriptions';

const WORKER_PATH = '/service-worker.js';
const PUBLIC_VAPID_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Detecta si el navegador soporta Web Push API
 * Nota: iOS (Safari y Chrome) solo soporta push en PWAs instaladas (iOS 16.4+)
 */
const isPushSupported = () => {
  // Verificar soporte básico
  if (!('serviceWorker' in navigator)) {
    return false;
  }
  
  if (!('PushManager' in window)) {
    return false;
  }

  // Verificar que Notification existe (iOS Chrome puede no tenerlo)
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return false;
  }

  // Detectar iOS (Safari, Chrome, etc. - todos usan WebKit)
  // Mejor detección que incluye iPadOS 13+ como iPad
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  if (isIOS) {
    // En iOS, solo funciona si está instalado como PWA (iOS 16.4+)
    // Verificar si está en modo standalone (PWA instalada)
    const standaloneFlag = (window.navigator as any).standalone === true;
    const displayStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const displayFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
    const isStandalone = standaloneFlag || displayStandalone || displayFullscreen;
    
    if (!isStandalone) {
      // iOS en navegador normal no soporta push
      return false;
    }
    // Verificar versión de iOS (requiere 16.4+)
    // En iOS 16.4+, PushManager está disponible en modo standalone
    // Si llegamos aquí y PushManager existe, asumimos que es compatible
  }

  return true;
};

/**
 * Obtiene información sobre el soporte de push y posibles limitaciones
 */
export const getPushSupportInfo = () => {
  // Mejor detección de iOS que incluye iPadOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isChrome = /chrome/i.test(navigator.userAgent) && !/edg/i.test(navigator.userAgent);
  
  // Detección mejorada de modo standalone (PWA instalada)
  const standaloneFlag = (window.navigator as any).standalone === true;
  const displayStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const displayFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
  const isStandalone = standaloneFlag || displayStandalone || displayFullscreen;
  
  const hasNotificationAPI = typeof window !== 'undefined' && 'Notification' in window;
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const supported = isPushSupported();
  
  return {
    supported,
    isIOS,
    isSafari,
    isChrome,
    isStandalone,
    hasNotificationAPI,
    hasServiceWorker,
    hasPushManager,
    requiresPWA: isIOS && !isStandalone,
    message: isIOS && !isStandalone
      ? 'Las notificaciones push en iOS requieren instalar la app como PWA. Agrega esta página a tu pantalla de inicio desde Safari.'
      : !hasNotificationAPI
      ? 'Tu navegador no soporta la API de Notificaciones.'
      : !hasServiceWorker
      ? 'Tu navegador no soporta Service Workers.'
      : !hasPushManager
      ? 'Tu navegador no soporta Push Manager.'
      : supported
      ? null
      : 'Tu navegador no soporta notificaciones push.',
  };
};

/**
 * Registra el service worker temprano (útil para iOS PWA)
 * Se puede llamar al iniciar la app, incluso antes de solicitar permisos
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const existing = await navigator.serviceWorker.getRegistration(WORKER_PATH);
    if (existing) {
      // Asegurar que esté activo
      if (existing.active) {
        // Service Worker activo
      } else if (existing.installing) {
        await new Promise((resolve) => {
          existing.installing!.addEventListener('statechange', () => {
            if (existing.installing!.state === 'activated') {
              resolve(null);
            }
          });
        });
      } else if (existing.waiting) {
        await existing.update();
      }
      return existing;
    }

    const registration = await navigator.serviceWorker.register(WORKER_PATH);
    
    // Esperar a que esté activo
    if (registration.installing) {
      await new Promise((resolve) => {
        registration.installing!.addEventListener('statechange', () => {
          if (registration.installing!.state === 'activated') {
            resolve(null);
          }
        });
      });
    }
    
    return registration;
  } catch (error) {
    return null;
  }
};

const getRegistration = async () => {
  if (!isPushSupported()) {
    return null;
  }

  // Intentar usar el registro existente o registrar uno nuevo
  let registration = await navigator.serviceWorker.getRegistration(WORKER_PATH);
  
  if (!registration) {
    registration = await navigator.serviceWorker.register(WORKER_PATH);
  }
  
  // Asegurar que el service worker esté activo
  if (registration) {
    if (registration.active) {
      // Service Worker activo
    } else if (registration.installing) {
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout esperando activación del service worker'));
        }, 10000);
        
        registration!.installing!.addEventListener('statechange', () => {
          if (registration!.installing!.state === 'activated') {
            clearTimeout(timeout);
            resolve();
          } else if (registration!.installing!.state === 'redundant') {
            clearTimeout(timeout);
            reject(new Error('Service worker se volvió redundante'));
          }
        });
      });
    } else if (registration.waiting) {
      await registration.update();
    }
  }
  
  return registration;
};

export const pushManager = {
  register: async (userId: string) => {
    const supportInfo = getPushSupportInfo();
    
    if (!supportInfo.supported) {
      if (supportInfo.requiresPWA) {
        return { success: false, reason: 'ios_pwa_required' } as const;
      }
      return { success: false, reason: 'unsupported' } as const;
    }

    if (!PUBLIC_VAPID_KEY) {
      return { success: false, reason: 'missing_vapid_key' } as const;
    }

    // Verificar que Notification existe antes de usarlo
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return { success: false, reason: 'unsupported' } as const;
    }

    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      return { success: false, reason: permission } as const;
    }

    const registration = await getRegistration();
    if (!registration) {
      return { success: false, reason: 'registration_failed' } as const;
    }

    if (!registration.active) {
      return { success: false, reason: 'service_worker_not_active' } as const;
    }

    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
        });
      } catch (error) {
        return { success: false, reason: 'subscription_failed', error: String(error) } as const;
      }
    }

    const saved = await pushSubscriptionsService.saveSubscription(userId, subscription);
    if (saved) {
      return { success: true as const };
    } else {
      return { success: false as const, reason: 'persist_failed' };
    }
  },

  unregister: async () => {
    if (!isPushSupported()) {
      return false;
    }

    const registration = await navigator.serviceWorker.getRegistration(WORKER_PATH);
    const subscription = await registration?.pushManager.getSubscription();

    if (subscription) {
      await pushSubscriptionsService.removeSubscription(subscription.endpoint);
      await subscription.unsubscribe();
    }

    return true;
  },

  showLocalNotification: async (payload: {
    title: string;
    body: string;
    url?: string;
  }) => {
    if (!isPushSupported()) {
      return;
    }

    // Verificar permisos antes de intentar mostrar la notificación
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission !== 'granted') {
        return;
      }
    }

    const registration = await getRegistration();
    const options = {
      body: payload.body,
      data: { url: payload.url || '/' },
    };

    if (registration?.active) {
      registration.active.postMessage({
        type: 'SHOW_NOTIFICATION',
        payload: {
          title: payload.title,
          options,
        },
      });
    } else if (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      Notification.permission === 'granted'
    ) {
      new Notification(payload.title, options);
    }
  },

  /**
   * Obtiene el estado actual de las notificaciones push
   * Retorna información sobre permisos, suscripción y soporte
   */
  getStatus: async () => {
    const supportInfo = getPushSupportInfo();
    
    let permission: NotificationPermission | 'not-available' = 'not-available';
    if (typeof window !== 'undefined' && 'Notification' in window) {
      permission = Notification.permission;
    }

    let hasSubscription = false;
    let subscriptionEndpoint: string | null = null;
    
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration(WORKER_PATH);
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          hasSubscription = !!subscription;
          subscriptionEndpoint = subscription?.endpoint || null;
        }
      } catch (error) {
        // Error silencioso al verificar suscripción
      }
    }

    return {
      permission,
      hasSubscription,
      subscriptionEndpoint,
      supportInfo,
    };
  },

  /**
   * Función de debug para diagnosticar problemas con push notifications
   * Llama desde la consola: window.pushDebug()
   */
  debug: async () => {
    const info = getPushSupportInfo();
    const swRegistration = await navigator.serviceWorker.getRegistration(WORKER_PATH);
    
    const debugInfo = {
      supportInfo: info,
      serviceWorker: {
        registered: !!swRegistration,
        active: !!swRegistration?.active,
        installing: !!swRegistration?.installing,
        waiting: !!swRegistration?.waiting,
        state: swRegistration?.active?.state || swRegistration?.installing?.state || swRegistration?.waiting?.state || 'none',
      },
      notificationPermission: typeof window !== 'undefined' && 'Notification' in window 
        ? Notification.permission 
        : 'not available',
      vapidKey: PUBLIC_VAPID_KEY ? 'configured' : 'missing',
      environment: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        isSecureContext: window.isSecureContext,
        location: window.location.href,
      },
    };
    
    return debugInfo;
  },
};

// Exponer función de debug globalmente para facilitar diagnóstico
if (typeof window !== 'undefined') {
  (window as any).pushDebug = pushManager.debug;
}

